/**
 * Employee Time Portal — API error classification and the friendly messages the portal
 * shows. The codes are the API's: the /portal front door (src/auth/resolvePortalEmployee.ts),
 * the punch guards (src/services/kioskPunchGuards.ts) and the atomic switch
 * (PUNCH_SWITCH_CODES in src/controllers/timeEntryHourly.ts). Pure (no axios import):
 * it reads the axios error shape structurally.
 */

export type PortalErrorKind =
  /** No response (offline, DNS, CORS, timeout) or a gateway 502/503/504: retry with the same body. */
  | 'network'
  /** 401: the bearer token is missing or expired. Never auto-retry; ask the user to sign in. */
  | 'sessionExpired'
  /** The portal itself refuses this user / tenant (503 PORTAL_*, 403 PORTAL_ACCESS_REQUIRED / OID_MISMATCH). */
  | 'unavailable'
  /** A definite refusal (4xx with or without a code). The write did not happen. */
  | 'refused'
  /** 500: the server failed. */
  | 'server';

export interface PortalErrorInfo {
  kind: PortalErrorKind;
  status: number | null;
  code: string | null;
  message: string;
}

export const PORTAL_ERROR_MESSAGES: Record<string, string> = {
  // punch guards (kioskPunchGuards.ts)
  OPEN_PUNCH_EXISTS: "You're already clocked in today. Refresh to see your open punch.",
  PUNCH_OVERLAP: "That punch overlaps another punch on your timesheet. Refresh and check today's punches.",
  WEEK_SUBMITTED: "This week's timesheet was submitted, so punches are locked. Ask your supervisor or payroll to unlock this week.",
  PAYROLL_EXPORTED: 'Payroll for this week was already exported, so punches are locked. Contact payroll to make changes.',
  PAY_PERIOD_CHANGE: 'That change would move the punch into a different week. Delete it and add it again in that week.',
  OUT_NOT_AFTER_IN: 'The out time must be after the in time.',
  OPEN_PUNCH: 'A punch is still open this week. Clock out (or fix the open punch) before you submit.',
  // the atomic switch (PUNCH_SWITCH_CODES)
  NOT_OPEN: 'That punch is already clocked out. Refresh to see your latest punches.',
  INVALID_PUNCH_STATE: 'Your punch status changed on another device. Refresh and try again.',
  OPEN_PUNCH_MISMATCH: 'Your open punches changed on another device. Refresh and try again.',
  NO_PAY_PERIOD: 'No pay period covers this time. Contact payroll.',
  INVALID_SWITCH: "That job choice isn't valid. Pick a project or cost code and try again.",
  NOT_FOUND: "That punch wasn't found. Refresh to see your latest punches.",
  INVALID_PAY_PERIOD: 'That week could not be found.',
  // the activity feed (/portal/activity)
  INVALID_WEEKS: 'Pick a range of 1 to 8 weeks.',
  PAY_PERIOD_NOT_FOUND: 'That week could not be found.',
  // the /portal front door (resolvePortalEmployee.ts)
  PORTAL_DISABLED: "The employee time portal isn't turned on yet. Keep tracking your time the way you do today.",
  PORTAL_MISCONFIGURED: "The employee time portal isn't set up correctly. Contact your administrator.",
  PORTAL_SCHEMA_MISSING: "The employee time portal isn't available yet. Contact your administrator.",
  PORTAL_ACCESS_REQUIRED: "Your account doesn't have employee time portal access. Contact payroll.",
  OID_MISMATCH: 'This email is linked to a different Microsoft account. Ask an administrator to reset your sign-in.',
  PORTAL_ACCESS_ERROR: "Couldn't verify your portal access. Try again in a moment.",
};

const UNAVAILABLE_CODES = new Set([
  'PORTAL_DISABLED',
  'PORTAL_MISCONFIGURED',
  'PORTAL_SCHEMA_MISSING',
  'PORTAL_ACCESS_REQUIRED',
  'OID_MISMATCH',
]);

export const NETWORK_MESSAGE = "Couldn't reach the server. Check your connection.";
export const SESSION_EXPIRED_MESSAGE = 'Your session expired. Sign in again to keep using the portal.';
export const SERVER_MESSAGE = 'Something went wrong on the server. Refresh and try again.';

/** 409 OPEN_PUNCH where only a legacy job (TimeEntry) punch is open: the portal can't close it. */
export const OPEN_JOB_PUNCH_MESSAGE = "You have an open job punch that can't be closed here — contact payroll.";

function idCount(value: unknown): number {
  return Array.isArray(value) ? value.length : 0;
}

/** The OPEN_PUNCH body names no open hourly punch but at least one open job (TimeEntry) punch. */
function isLegacyJobPunchOnly(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  const d = data as { openTimeEntryHourlyIDs?: unknown; openTimeEntryIDs?: unknown };
  return idCount(d.openTimeEntryHourlyIDs) === 0 && idCount(d.openTimeEntryIDs) > 0;
}

/** `8:18 AM` of an ISO instant at its own stored offset (`-07:00`) — the punch's wall clock, no zone needed. */
export function wallClock(iso: unknown, offset: unknown): string | null {
  const ms = typeof iso === 'string' || iso instanceof Date ? Date.parse(String(iso instanceof Date ? iso.toISOString() : iso)) : NaN;
  if (Number.isNaN(ms)) return null;
  const m = typeof offset === 'string' ? /^([+-])(\d{2}):?(\d{2})$/.exec(offset.trim()) : null;
  const offMin = m ? (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3])) : 0;
  const d = new Date(ms + offMin * 60000);
  const h = d.getUTCHours();
  return `${h % 12 || 12}:${String(d.getUTCMinutes()).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

/** "That overlaps your 8:18 AM – 9:30 AM punch." (meal / open punch worded to match). */
export function overlapMessage(fromLabel: string, toLabel: string | null, meal: boolean): string {
  const what = meal ? 'meal' : 'punch';
  return toLabel
    ? `That overlaps your ${fromLabel} – ${toLabel} ${what}.`
    : `That overlaps your open ${what} from ${fromLabel}.`;
}

/** 409 PUNCH_OVERLAP names the conflicting row (kioskPunchGuards assertNoOverlap → conflictingPunch); null when it doesn't. */
function overlapFromBody(data: unknown): string | null {
  const p = (data as { conflictingPunch?: Record<string, unknown> } | null)?.conflictingPunch;
  if (!p || typeof p !== 'object') return null;
  const from = wallClock(p.inDateTime, p.inDateTimeOffset);
  if (!from) return null;
  const to = p.outDateTime != null ? wallClock(p.outDateTime, p.outDateTimeOffset ?? p.inDateTimeOffset) : null;
  return overlapMessage(from, to, Number(p.type) === 2);
}

interface AxiosLikeError {
  response?: { status?: number; data?: unknown };
  message?: string;
}

/** The friendly message for a code, else null. */
export function messageForCode(code: string | null | undefined): string | null {
  return code && Object.prototype.hasOwnProperty.call(PORTAL_ERROR_MESSAGES, code) ? PORTAL_ERROR_MESSAGES[code] : null;
}

export function describePortalError(err: unknown): PortalErrorInfo {
  const e = (err ?? {}) as AxiosLikeError;
  const response = e.response;
  if (!response || typeof response.status !== 'number') {
    return { kind: 'network', status: null, code: null, message: NETWORK_MESSAGE };
  }
  const status = response.status;
  const data = (response.data && typeof response.data === 'object' ? response.data : {}) as { code?: unknown; message?: unknown };
  const code = typeof data.code === 'string' ? data.code : null;
  const serverMessage = typeof data.message === 'string' && data.message.trim() ? data.message.trim() : null;

  if (status === 401) return { kind: 'sessionExpired', status, code, message: SESSION_EXPIRED_MESSAGE };
  if (code && UNAVAILABLE_CODES.has(code)) {
    return { kind: 'unavailable', status, code, message: messageForCode(code) ?? serverMessage ?? SERVER_MESSAGE };
  }
  if (code === 'PORTAL_ACCESS_ERROR' || status === 502 || status === 503 || status === 504) {
    return { kind: 'network', status, code, message: messageForCode(code) ?? NETWORK_MESSAGE };
  }
  if (status >= 500) return { kind: 'server', status, code, message: SERVER_MESSAGE };
  if (code === 'PUNCH_OVERLAP') {
    const specific = overlapFromBody(response.data);
    if (specific) return { kind: 'refused', status, code, message: specific };
  }
  if (code === 'OPEN_PUNCH' && isLegacyJobPunchOnly(response.data)) {
    return { kind: 'refused', status, code, message: OPEN_JOB_PUNCH_MESSAGE };
  }
  return { kind: 'refused', status, code, message: messageForCode(code) ?? serverMessage ?? 'That request was refused.' };
}

/** A punch is resent automatically only for a network-class failure (and never without a token). */
export function isRetryable(info: PortalErrorInfo): boolean {
  return info.kind === 'network';
}

/**
 * What the Today page does with its pending punch after a failed send:
 *   signIn  401 — keep it, open the session modal, send after sign-in;
 *   keep    network or 5xx / unknown server error — keep it (same clientPunchID, same body)
 *           in the failed state with the Retry strip; the API's ClientPunchID replay makes
 *           a resend of a write that actually landed harmless;
 *   drop    a definite refusal (4xx, or the portal itself unavailable) — the write did not
 *           happen; show the server's message.
 */
export type PunchFailureAction = 'signIn' | 'keep' | 'drop';

export function punchFailureAction(info: PortalErrorInfo): PunchFailureAction {
  if (info.kind === 'sessionExpired') return 'signIn';
  if (info.kind === 'network' || info.kind === 'server') return 'keep';
  return 'drop';
}
